import React, { useState, useCallback, useEffect } from "react";
import { RouteComponentProps, navigate } from "@reach/router";
import { FiArrowLeft, FiEdit3 } from "react-icons/fi";
import { TabComponent } from "newui/components/TabComponent/TabComponent";
import type { TabMenuItem } from "newui/components/TabComponent/TabComponent";
import SkeletonBase from "newui/components/Skeleton";
import { useSupplierDetail } from "../hooks/useSupplierDetail";
import { useSupplierPerformancePermissions } from "../hooks/useSupplierPerformancePermissions";
import { useExportPdf } from "../hooks/useExportPdf";
import { useDeleteRating } from "../hooks/useDeleteRating";
import {
	SupplierOverview,
	RatingsList,
} from "../components/SupplierDetail";
import { PdfExportButton } from "../components/PdfExportButton";
import { DeleteRatingModal } from "../components/DeleteRatingModal";
import {
	trackSupplierDetailViewed,
	trackSupplierViewedBeforeRfq,
} from "../utils/analytics";
import type { SupplierRating } from "../types/supplier-performance.types";
import { CompanyOverviewTab } from "../components/SupplierDetail/CompanyOverviewTab";

interface SupplierDetailPageProps extends RouteComponentProps {
	supplierId?: string;
}

const TAB_IDS = {
	RATINGS: "ratings",
	OVERVIEW: "overview",
} as const;

const TABS: TabMenuItem[] = [
	{ id: TAB_IDS.RATINGS, name: "Ratings", testId: "tab-ratings" },
	{ id: TAB_IDS.OVERVIEW, name: "Company Overview", testId: "tab-overview" },
];

export const SupplierDetailPage: React.FC<SupplierDetailPageProps> = ({
	supplierId,
}) => {
	const [activeTab, setActiveTab] = useState<string>(TAB_IDS.RATINGS);
	const [deletingRating, setDeletingRating] = useState<SupplierRating | null>(null);
	const parsedId = supplierId ? parseInt(supplierId, 10) : null;
	const { data, isLoading, isError } = useSupplierDetail(parsedId);
	const { canCreate, canDelete, canExport } = useSupplierPerformancePermissions();
	const { exportSupplier, isExporting } = useExportPdf();
	const { mutate: deleteRatingMutate, isPending: isDeleting } = useDeleteRating({
		onSuccess: () => {
			setDeletingRating(null);
		},
	});

	useEffect(() => {
		if (parsedId) {
			trackSupplierDetailViewed(parsedId);
			trackSupplierViewedBeforeRfq(parsedId);
		}
	}, [parsedId]);

	const handleTabChange = useCallback((_name: string, id: string) => {
		setActiveTab(id);
	}, []);

	const handleBack = useCallback(() => {
		navigate("../my-suppliers");
	}, []);

	const handleRate = useCallback(() => {
		if (parsedId) {
			navigate(`../my-suppliers/rate/${parsedId}`);
		}
	}, [parsedId]);

	const handleExport = useCallback(() => {
		if (data) {
			exportSupplier(data.supplier_company_id, data.company_name);
		}
	}, [data, exportSupplier]);

	const handleDeleteConfirm = useCallback(() => {
		if (deletingRating) {
			deleteRatingMutate(deletingRating.id);
		}
	}, [deletingRating, deleteRatingMutate]);

	if (isLoading) {
		return (
			<div className="space-y-4 p-6" data-testid="supplier-detail-loading">
				<SkeletonBase height="40px" width="200px" />
				<SkeletonBase height="100px" />
				<SkeletonBase height="300px" />
			</div>
		);
	}

	if (isError || !data) {
		return (
			<div className="p-6" data-testid="supplier-detail-error">
				<div className="rounded-lg bg-red-50 p-6 text-center">
					<p className="text-red-600">
						Failed to load supplier details. Please try again.
					</p>
					<button
						type="button"
						className="mt-4 text-sm font-medium text-blue-600 hover:text-blue-800"
						onClick={handleBack}
					>
						Back to Supplier Hub
					</button>
				</div>
			</div>
		);
	}

	const ratings = data.ratings ?? [];

	const tabMenus: TabMenuItem[] = TABS.map((tab) => {
		if (tab.id === TAB_IDS.RATINGS) {
			return {
				...tab,
				showBadge: true,
				total: ratings.length,
			};
		}
		return tab;
	});

	return (
		<div className="p-6" data-testid="supplier-detail-page">
			<div className="mb-6 flex items-center justify-between">
				<button
					type="button"
					className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
					onClick={handleBack}
					data-testid="back-to-suppliers"
				>
					<FiArrowLeft className="h-4 w-4" />
					Back to Supplier Hub
				</button>
				<div className="flex items-center gap-3">
					{canExport && (
						<PdfExportButton
							onClick={handleExport}
							isExporting={isExporting}
							label="Export PDF"
						/>
					)}
					{canCreate && (
						<button
							type="button"
							className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
							onClick={handleRate}
							data-testid="rate-supplier-btn"
						>
							<FiEdit3 className="h-4 w-4" />
							Rate Supplier
						</button>
					)}
				</div>
			</div>

			<SupplierOverview supplier={data} />

			<div className="mt-6">
				<TabComponent
					menus={tabMenus}
					activeTab={activeTab}
					changeActiveTab={handleTabChange}
				/>

				<div className="mt-4">
					{activeTab === TAB_IDS.OVERVIEW && (
						<CompanyOverviewTab supplier={data} />
					)}
					{activeTab === TAB_IDS.RATINGS && (
						<RatingsList
							ratings={ratings}
							canDelete={canDelete}
							onDeleteClick={setDeletingRating}
						/>
					)}
				</div>
			</div>

			<DeleteRatingModal
				rating={deletingRating}
				isOpen={deletingRating !== null}
				isDeleting={isDeleting}
				onClose={() => setDeletingRating(null)}
				onConfirm={handleDeleteConfirm}
			/>
		</div>
	);
};
